import { useCallback, useEffect, useMemo, useState } from 'react'

import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import { FormGroup, Input, Label, Radio } from '~/components/unform'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import InvestmentObjective from '~/modules/retailMedia/components/CampaignForm/elements/InvestmentObjective'
import StandardInvestment from '~/modules/retailMedia/components/CampaignForm/elements/OffsiteInvestment/Standard'
import Projections from '~/modules/retailMedia/components/CampaignForm/elements/Projections'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'

import BalanceByPublisher from '../../../BalanceByPublisher'
import { useCheckingAccountIdForBalance } from '../../hooks/useCheckingAccountIdForBalance'
import TotalInvestment from './TotalBudget'

interface OffsiteInvestmentProps {
  publisherId: string
  formRef: React.RefObject<FormHandles>
  formDataFinal: Partial<DTOPayloadMinCosts>
  formIsValid: FormIsValidResponse
  setSectionIsValid: (isValid: boolean) => void
  sectionIsValid: boolean
  // Lifts the frozen projection snapshot for dto.ts (FR-038). Optional,
  // defaulting to a no-op: the legacy flow (no channel) never produces one.
  onProjectionsChange?: (snapshot: Record<string, unknown> | null) => void
  // Quando informado, define se o painel começa aberto (padrão: aberto)
  initialCollapsibleState?: boolean
}

export const OffsiteInvestment = ({
  publisherId,
  formRef,
  formDataFinal,
  formIsValid,
  setSectionIsValid,
  sectionIsValid,
  onProjectionsChange,
  initialCollapsibleState
}: OffsiteInvestmentProps) => {
  const { t } = useAPPTranslation()
  const { superAdmin, lengthCentsCurrency } = useAppInfo()
  const { allowOffsiteBriefing, selectedChannel } = useCampaignForm()
  const checkingAccountId = useCheckingAccountIdForBalance()
  const [selectedInvestmentType, setSelectedInvestmentType] = useState<
    string | null
  >(null)

  const [childIsValid, setChildIsValid] = useState(false)
  const [costFactorIsValid, setCostFactorIsValid] = useState(true)

  const [selectedObjective, setSelectedObjective] =
    useState<InvestmentObjectiveType | null>(null)
  const [hasReferenceError, setHasReferenceError] = useState(false)
  const [budgetInReais, setBudgetInReais] = useState(0)
  const [projectionsSnapshot, setProjectionsSnapshot] = useState<Record<
    string,
    unknown
  > | null>(null)

  useEffect(() => {
    setSelectedInvestmentType(formRef.current?.getFieldValue('cost_type'))
  }, [formRef])

  // FR-027a: the new flow fixes the investment type as always Total,
  // without the operator ever choosing -- the field itself keeps existing
  // and persisting (FR-027d preserves the choice for the legacy flow).
  useEffect(() => {
    if (!allowOffsiteBriefing) return

    formRef.current?.setFieldValue('cost_type', 'total_budget')
    setSelectedInvestmentType('total_budget')
  }, [allowOffsiteBriefing, formRef])

  // New flow's overall investment validity also requires an objective, a
  // captured projection, and a working reference catalog (FR-030d) -- not
  // just a positive budget. A persistent catalog failure blocks the step
  // the same way a simulation failure already does, even if the operator
  // already picked an objective before the catalog call failed.
  const isNewFlowReady = useMemo(
    () =>
      childIsValid &&
      !!selectedObjective &&
      !!projectionsSnapshot &&
      !hasReferenceError,
    [childIsValid, selectedObjective, projectionsSnapshot, hasReferenceError]
  )

  useEffect(() => {
    const isValid = allowOffsiteBriefing ? isNewFlowReady : childIsValid

    setSectionIsValid(isValid && costFactorIsValid)
  }, [
    allowOffsiteBriefing,
    isNewFlowReady,
    childIsValid,
    costFactorIsValid,
    setSectionIsValid
  ])

  useEffect(() => {
    onProjectionsChange?.(projectionsSnapshot)
  }, [projectionsSnapshot, onProjectionsChange])

  const handleSelectedInvestmentType = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedInvestmentType(event.target.value)
    },
    []
  )

  const handleCostFactorChange = useCallback(
    (inputData: { value: string; name: string }) => {
      const value = Number(inputData.value)
      setCostFactorIsValid(!!inputData.value && value >= 1 && value <= 100)
    },
    []
  )

  const handleBudgetChange = useCallback(
    (values: InputMoneyValuesOnChange) => {
      const raw = values?.floatValue
      const value = lengthCentsCurrency ? raw / 100 : raw
      setBudgetInReais(value || 0)
    },
    [lengthCentsCurrency]
  )

  const investmentOptions: RadioOptionProps[] = useMemo(
    () => [
      {
        id: 'standard',
        value: 'standard',
        label: t('rm:campaign.create.InvestmentType.radio.daily.label'),
        description: t(
          'rm:campaign.create.InvestmentType.radio.daily.description'
        )
      },
      {
        id: 'total_budget',
        value: 'total_budget',
        label: t('rm:campaign.create.InvestmentType.radio.total.label'),
        description: t(
          'rm:campaign.create.InvestmentType.radio.total.description'
        )
      }
    ],
    []
  )

  return (
    <Panel
      title={t('rm:campaign.create.Investment.title')}
      description={t('rm:budgetDescription')}
      isCollapsible
      initialCollapsibleState={initialCollapsibleState}
      iconLeft={<BadgeIsValid isValid={sectionIsValid} />}
      headerSideContent={
        <BalanceByPublisher
          id={publisherId}
          idAccountType="industry"
          checkingAccountId={checkingAccountId}
        />
      }
    >
      {allowOffsiteBriefing && selectedChannel && (
        <InvestmentObjective
          channel={selectedChannel}
          onChange={setSelectedObjective}
          onReferenceError={setHasReferenceError}
        />
      )}

      {/* Investment type choice only exists in the legacy flow (FR-027a) --
          the new flow fixes it to Total without a UI to change it. */}
      {!allowOffsiteBriefing && (
        <FormGroup className="mb-3">
          <label htmlFor="type">
            {t('rm:campaign.create.InvestmentType.radio.label')}
          </label>
          <Radio
            options={investmentOptions}
            name="cost_type"
            layout="responsive-flex"
            required
            onChange={handleSelectedInvestmentType}
          />
        </FormGroup>
      )}
      {/* Gated on !allowOffsiteBriefing too (not just selectedInvestmentType):
          'standard' is legacy-only (FR-027a), but the forcing effect above
          only resets selectedInvestmentType away from a stale 'standard'
          value *after* allowOffsiteBriefing flips true -- without this
          guard, that one-render gap double-mounts this alongside
          TotalInvestment below, both driving the same setChildIsValid. */}
      {!allowOffsiteBriefing && selectedInvestmentType === 'standard' && (
        <StandardInvestment
          adType="banner"
          publisherId={publisherId}
          formData={formDataFinal}
          formIsValid={formIsValid}
          handleIsValid={setChildIsValid}
          initialValueDailyBudget={'R$ 100.00'}
        />
      )}
      {/* allowOffsiteBriefing alone (not just selectedInvestmentType) is
          enough to show this in the new flow: cost_type's Radio is never
          rendered there (it's hidden by design, FR-027a), so the effect
          above that syncs selectedInvestmentType from it only runs after
          allowOffsiteBriefing itself flips true -- waiting on the state
          round-trip left a one-render gap where neither this nor
          StandardInvestment showed (Copilot review, 2026-09-04). */}
      {(selectedInvestmentType === 'total_budget' || allowOffsiteBriefing) && (
        <TotalInvestment
          handleIsValid={setChildIsValid}
          formRef={formRef}
          formIsValid={formIsValid}
          disabled={!formIsValid?.isValid}
          onBudgetChange={allowOffsiteBriefing ? handleBudgetChange : undefined}
        />
      )}

      {allowOffsiteBriefing && selectedChannel && (
        // TotalInvestment above is a bare field, not a Panel (no margin of
        // its own) -- this spacer is what separates it from Projections'
        // panel below (reference screenshot 2026-09-01).
        <div className="mt-3">
          <Projections
            channel={selectedChannel}
            objective={selectedObjective}
            budget={budgetInReais}
            onChange={setProjectionsSnapshot}
          />
        </div>
      )}

      {!!superAdmin && (
        <FormGroup className="border-top pt-2">
          <Label
            htmlFor="markup_factor"
            isRequired
            helperText={t('rm:costFactor.description')}
          >
            {t('rm:costFactor.label')}
          </Label>
          <Input
            id="markup_factor"
            name="markup_factor"
            type="number"
            min={1}
            max={100}
            step={1}
            required
            onChange={handleCostFactorChange}
            showBadgeIsInvalid={!costFactorIsValid}
          />
          {!costFactorIsValid && (
            <Alert variant="warning">{t('rm:costFactor.validation')}</Alert>
          )}
        </FormGroup>
      )}
    </Panel>
  )
}
